import React, { useMemo, useState, Fragment, Component } from 'react';
import { MapContainer, TileLayer, CircleMarker, Popup } from 'react-leaflet';
import { Link } from 'react-router-dom';
import { Layers, Filter, Calendar, Flame, Users, FileText, ShieldAlert, AlertTriangle, Activity, Gauge, MapPin } from 'lucide-react';
import { BINDA_LIST, KATEGORI_LAPORAN, SEMUA_STATUS, STATUS_META, statusColor, type StatusWilayah } from '../data/binda';
type LayerKey = 'heatmap' | 'personel' | 'laporan' | 'status' | 'prioritas' | 'darurat' | 'aktivitas' | 'kpi';
interface LayerDef {
  key: LayerKey;
  label: string;
  icon: ComponentType<{
    className?: string;
  }>;
}
const LAYERS: LayerDef[] = [{
  key: 'status',
  label: 'Status Wilayah',
  icon: Activity
}, {
  key: 'heatmap',
  label: 'Heatmap Nasional',
  icon: Flame
}, {
  key: 'personel',
  label: 'Sebaran Personel',
  icon: Users
}, {
  key: 'laporan',
  label: 'Sebaran Laporan',
  icon: FileText
}, {
  key: 'prioritas',
  label: 'Laporan Prioritas',
  icon: ShieldAlert
}, {
  key: 'darurat',
  label: 'Laporan Darurat',
  icon: AlertTriangle
}, {
  key: 'aktivitas',
  label: 'Aktivitas Operasional',
  icon: MapPin
}, {
  key: 'kpi',
  label: 'KPI Wilayah',
  icon: Gauge
}];
const PROVINSI_OPTS = ['Semua Provinsi', ...BINDA_LIST.map(b => b.provinsi)];
const BINDA_OPTS = ['Semua BINDA', ...BINDA_LIST.map(b => b.binda)];
interface Props {
  height?: number | string;
  compact?: boolean;
}
export function PetaOperasiNasional({
  height = 480,
  compact = false
}: Props) {
  const [activeLayers, setActiveLayers] = useState<Record<LayerKey, boolean>>({
    status: true,
    heatmap: true,
    personel: false,
    laporan: false,
    prioritas: true,
    darurat: true,
    aktivitas: false,
    kpi: false
  });
  const [tanggal, setTanggal] = useState('2026-06-23');
  const [kategori, setKategori] = useState('Semua Kategori');
  const [provinsi, setProvinsi] = useState('Semua Provinsi');
  const [binda, setBinda] = useState('Semua BINDA');
  const toggleLayer = (key: LayerKey) => setActiveLayers(prev => ({
    ...prev,
    [key]: !prev[key]
  }));
  const filtered = useMemo(() => {
    return BINDA_LIST.filter(b => {
      if (provinsi !== 'Semua Provinsi' && b.provinsi !== provinsi) return false;
      if (binda !== 'Semua BINDA' && b.binda !== binda) return false;
      return true;
    });
  }, [provinsi, binda]);
  const selectClass = 'bg-black/40 border border-border/50 rounded px-2 py-1.5 text-[11px] font-mono text-foreground focus:outline-none focus:border-primary/50 w-full';
  return <div className="flex flex-col lg:flex-row h-full w-full" style={{
    minHeight: height
  }} data-id="element-437">
      {/* Control Rail */}
      {!compact && <div className="lg:w-60 shrink-0 border-b lg:border-b-0 lg:border-r border-border/50 bg-black/30 p-3 space-y-4 overflow-y-auto custom-scrollbar" data-id="element-438">
          {/* Filters */}
          <div data-id="element-439">
            <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest text-primary mb-2" data-id="element-440">
              <Filter className="w-3 h-3" data-id="element-441" /> Filter Operasi
            </div>
            <div className="space-y-2" data-id="element-442">
              <label className="block" data-id="element-443">
                <span className="text-[9px] font-mono uppercase text-muted-foreground flex items-center gap-1 mb-1" data-id="element-444">
                  <Calendar className="w-3 h-3" data-id="element-445" /> Filter Tanggal
                </span>
                <input type="date" value={tanggal} onChange={e => setTanggal(e.target.value)} className={selectClass} data-id="element-446" />
              </label>
              <label className="block" data-id="element-447">
                <span className="text-[9px] font-mono uppercase text-muted-foreground mb-1 block" data-id="element-448">
                  Filter Kategori
                </span>
                <select value={kategori} onChange={e => setKategori(e.target.value)} className={selectClass} data-id="element-449">
                  <option data-id="element-450">Semua Kategori</option>
                  {KATEGORI_LAPORAN.map(k => <option key={k} data-id="element-451">{k}</option>)}
                </select>
              </label>
              <label className="block" data-id="element-452">
                <span className="text-[9px] font-mono uppercase text-muted-foreground mb-1 block" data-id="element-453">
                  Filter Provinsi
                </span>
                <select value={provinsi} onChange={e => {
              setProvinsi(e.target.value);
              setBinda('Semua BINDA');
            }} className={selectClass} data-id="element-454">
                  {PROVINSI_OPTS.map(p => <option key={p} data-id="element-455">{p}</option>)}
                </select>
              </label>
              <label className="block" data-id="element-456">
                <span className="text-[9px] font-mono uppercase text-muted-foreground mb-1 block" data-id="element-457">
                  Filter BINDA
                </span>
                <select value={binda} onChange={e => setBinda(e.target.value)} className={selectClass} data-id="element-458">
                  {BINDA_OPTS.map(b => <option key={b} data-id="element-459">{b}</option>)}
                </select>
              </label>
            </div>
          </div>

          {/* Layer toggles */}
          <div data-id="element-460">
            <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest text-primary mb-2" data-id="element-461">
              <Layers className="w-3 h-3" data-id="element-462" /> Layer Peta
            </div>
            <div className="space-y-1" data-id="element-463">
              {LAYERS.map(l => {
            const Icon = l.icon;
            const on = activeLayers[l.key];
            return <button key={l.key} onClick={() => toggleLayer(l.key)} className={`w-full flex items-center justify-between px-2 py-1.5 rounded text-[10px] font-mono transition-colors border ${on ? 'bg-primary/10 border-primary/40 text-primary' : 'border-transparent text-muted-foreground hover:bg-white/5'}`} data-id="element-464">
                    <span className="flex items-center gap-2" data-id="element-465">
                      <Icon className="w-3 h-3" data-id="element-466" /> {l.label}
                    </span>
                    <span className={`w-1.5 h-1.5 rounded-full ${on ? 'bg-primary shadow-[0_0_6px_var(--primary)]' : 'bg-muted-foreground/40'}`} data-id="element-467" />
                  </button>;
          })}
            </div>
          </div>

          {/* Counter */}
          <div className="text-[9px] font-mono text-muted-foreground border-t border-border/40 pt-2" data-id="element-468">
            {filtered.length} dari {BINDA_LIST.length} BINDA ditampilkan
          </div>
        </div>}

      {/* Map */}
      <div className="relative flex-1 bg-[#0a0e17] min-h-[300px]" data-id="element-469">
        <MapContainer center={[-2.5489, 118.0149]} zoom={5} style={{
        height: '100%',
        width: '100%',
        background: 'transparent'
      }} zoomControl={false} attributionControl={false} data-id="element-470">
          <TileLayer url="https://{s}.basemaps.cartocdn.com/dark_nolabels/{z}/{x}/{y}{r}.png" data-id="element-471" />

          {filtered.map((b, idx) => {
          const color = statusColor(b.status);
          const nodes: React.ReactNode[] = [];
          // Heatmap layer — large translucent activity blob
          if (activeLayers.heatmap) {
            nodes.push(<CircleMarker key={`heat-${idx}`} center={[b.lat, b.lng]} radius={Math.max(14, b.laporanHariIni / 4)} pathOptions={{
              color,
              fillColor: color,
              fillOpacity: 0.12,
              weight: 0
            }} data-id="element-472" />);
          }
          // Personnel distribution
          if (activeLayers.personel) {
            nodes.push(<CircleMarker key={`pers-${idx}`} center={[b.lat, b.lng]} radius={Math.max(5, b.personelAktif / 60)} pathOptions={{
              color: 'var(--status-info)',
              fillColor: 'var(--status-info)',
              fillOpacity: 0.25,
              weight: 1
            }} data-id="element-473" />);
          }
          // Report distribution
          if (activeLayers.laporan) {
            nodes.push(<CircleMarker key={`lap-${idx}`} center={[b.lat, b.lng]} radius={Math.max(5, b.laporanHariIni / 12)} pathOptions={{
              color: 'var(--primary)',
              fillColor: 'var(--primary)',
              fillOpacity: 0.2,
              weight: 1
            }} data-id="element-474" />);
          }
          // KPI layer — ring sized by KPI
          if (activeLayers.kpi) {
            nodes.push(<CircleMarker key={`kpi-${idx}`} center={[b.lat, b.lng]} radius={(b.kpiWilayah - 70) * 1.4} pathOptions={{
              color: 'var(--status-active)',
              fill: false,
              weight: 1,
              dashArray: '3 4'
            }} data-id="element-475" />);
          }
          // Operational activity pulse
          if (activeLayers.aktivitas) {
            nodes.push(<CircleMarker key={`akt-${idx}`} center={[b.lat, b.lng]} radius={6} className="animate-pulse" pathOptions={{
              color: 'var(--primary)',
              fillColor: 'var(--primary)',
              fillOpacity: 0.6,
              weight: 1
            }} data-id="element-476" />);
          }
          // Priority reports halo
          if (activeLayers.prioritas && b.laporanPrioritas >= 5) {
            nodes.push(<CircleMarker key={`pri-${idx}`} center={[b.lat, b.lng]} radius={16 + b.laporanPrioritas} pathOptions={{
              color: 'var(--status-standby)',
              fill: false,
              weight: 1,
              dashArray: '4'
            }} data-id="element-477" />);
          }
          // Emergency reports — red pulsing ring
          if (activeLayers.darurat && b.laporanDarurat > 0) {
            nodes.push(<CircleMarker key={`dar-${idx}`} center={[b.lat, b.lng]} radius={22} className="animate-ping" pathOptions={{
              color: 'var(--destructive)',
              fill: false,
              weight: 1.5,
              dashArray: '2 6'
            }} data-id="element-478" />);
          }
          // Base status marker (always when status layer on)
          if (activeLayers.status) {
            nodes.push(<CircleMarker key={`stat-${idx}`} center={[b.lat, b.lng]} radius={b.status === 'Kritis' ? 11 : b.status === 'Waspada' ? 9 : 7} className={b.status === 'Kritis' ? 'animate-pulse' : ''} pathOptions={{
              color,
              fillColor: color,
              fillOpacity: 0.55,
              weight: 2
            }} data-id="element-479">
                  <Popup className="glass-panel" data-id="element-480">
                    <div className="p-1 min-w-[160px]" data-id="element-481">
                      <div className="font-bold text-xs uppercase mb-1" data-id="element-482">
                        {b.binda}
                      </div>
                      <div className="text-[10px] font-mono text-muted-foreground mb-1" data-id="element-483">
                        {b.provinsi}
                      </div>
                      <div className="text-[10px] font-mono" data-id="element-484">
                        Status:{' '}
                        <span style={{
                      color
                    }} data-id="element-485">
                          {STATUS_META[b.status].dot} {b.status}
                        </span>
                      </div>
                      <div className="text-[10px] font-mono text-muted-foreground" data-id="element-486">
                        Personel Aktif: {b.personelAktif}
                      </div>
                      <div className="text-[10px] font-mono text-muted-foreground" data-id="element-487">
                        Laporan Hari Ini: {b.laporanHariIni}
                      </div>
                      <div className="text-[10px] font-mono text-muted-foreground" data-id="element-488">
                        Laporan Prioritas: {b.laporanPrioritas}
                      </div>
                      <div className="text-[10px] font-mono text-muted-foreground" data-id="element-489">
                        KPI Wilayah: {b.kpiWilayah}
                      </div>
                      <Link to={`/regional/${b.kode}`} className="mt-2 block w-full text-center bg-primary/20 text-primary border border-primary/50 rounded py-1 text-[10px] font-mono hover:bg-primary/30 transition-colors" data-id="element-490">
                        LIHAT DETAIL
                      </Link>
                    </div>
                  </Popup>
                </CircleMarker>);
          }
          return <Fragment key={idx} data-id="element-491">{nodes}</Fragment>;
        })}
        </MapContainer>

        {/* Status legend */}
        <div className="absolute bottom-3 left-3 z-[400] glass-panel-heavy p-2.5 rounded border border-border/50" data-id="element-492">
          <div className="text-[9px] font-mono uppercase tracking-widest text-muted-foreground mb-1.5" data-id="element-493">
            Status Wilayah
          </div>
          <div className="space-y-1" data-id="element-494">
            {SEMUA_STATUS.map((s: StatusWilayah) => <div key={s} className="flex items-center gap-2 text-[10px] font-mono" data-id="element-495">
                <span className="w-2 h-2 rounded-full" style={{
              backgroundColor: STATUS_META[s].color,
              boxShadow: `0 0 5px ${STATUS_META[s].color}`
            }} data-id="element-496" />
                {STATUS_META[s].dot} {s}
              </div>)}
          </div>
        </div>
      </div>
    </div>;
}